import { useStoreon } from 'storeon/react';
import { State } from 'store/types';
import { MOSCOW } from 'common/constants/region-codes';
import { MODEL_JSON_STORE_NAME, PROEKTIROVANIE_LINK, RESHENIYA_LINK } from './model-json-constants';
import { Breadcrumb, ModelJson, ModelJsonStore, PromoBlock, SeoProperties, SpecialOffer } from './model-json-types';
import { checkIfDateRangeActual } from './model-json-utils';

/**
 * Возвращает данные для хлебных крошек
 * @param {ModelJsonStore} modelJson
 * @returns {Breadcrumb[] | undefined}
 */
export const selectBreadcrumbs = (modelJson: ModelJsonStore): Breadcrumb[] | undefined =>
  modelJson?.model?.breadcrumbs?.filter(
    ({ link }) => link !== PROEKTIROVANIE_LINK && link !== RESHENIYA_LINK
  );

/**
 * Возвращает код региона
 * @param {ModelJsonStore} modelJson
 * @returns {string}
 */
export const selectRegionId = (modelJson: ModelJsonStore): string =>
  modelJson?.model?.properties.regionId || MOSCOW;

/**
 * Возвращает SEO данные
 * @param modelJson
 * @returns {SeoProperties | undefined}
 */
export const selectSeoProperties = (modelJson: ModelJsonStore): SeoProperties | undefined =>
  modelJson.model?.seoProperties;

/**
 * Возвращает код для Google аналитики
 * @param {ModelJsonStore} modelJson
 * @returns {string | undefined}
 */
export const selectGtmCode = (modelJson: ModelJsonStore): string | undefined =>
  modelJson?.model?.analytics?.gtmCode;

/**
 * Возвращает код для Яндекс аналитики
 * @param {ModelJsonStore} modelJson
 * @returns {string | undefined}
 */
export const selectYaCode = (modelJson: ModelJsonStore): string | undefined =>
  modelJson?.model?.analytics?.yaCode;

/**
 * Возвращает относительный урл текущей страницы
 * @param {ModelJsonStore} modelJson
 * @returns {string | undefined}
 */
export const selectCurrentUrl = (modelJson: ModelJsonStore): string | undefined =>
  modelJson?.model?.breadcrumbs?.[modelJson?.model?.breadcrumbs?.length - 1].link;

export const useModelJsonSelector = () => {
  const { modelJson } = useStoreon<State>(MODEL_JSON_STORE_NAME);

  if (modelJson && modelJson.model) {
    return modelJson.model;
  }

  return {} as ModelJson;
};

export const useAvailableOffersSelector = (): SpecialOffer[] => {
  const { specialOffer } = useModelJsonSelector();

  if (!specialOffer) {
    return [];
  }

  return specialOffer.filter(({ startDate, endDate }) => checkIfDateRangeActual(startDate, endDate));
};

export const usePromoBlockSelector = (position: number): PromoBlock | undefined => {
  const { promoBlock } = useModelJsonSelector();

  if (!promoBlock || !promoBlock[position]) {
    return undefined;
  }

  const promoBlockData = promoBlock[position];
  if (checkIfDateRangeActual(promoBlockData.startDate, promoBlockData.endDate)) {
    return promoBlockData;
  }

  return undefined;
};

export const useRegionIdSelector = () => {
  const { properties } = useModelJsonSelector();

  return properties?.regionId || MOSCOW;
};

export const useRegionsSelector = () => {
  const { regions } = useModelJsonSelector();

  return regions;
};
